import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { useBlogPosts } from "@/hooks/useBlog";
import { relevanceScore, MIN_RELEVANCE_SCORE } from "@/lib/keywordMatch";

interface BlogStripProps {
  limit?: number;
  /** When provided, posts are relevance-matched to this product instead of sorted by date. */
  relatedToProduct?: {
    name: string;
    categories?: string | null;
    id?: number | string;
  } | null;
  heading?: string;
  subheading?: string;
  className?: string;
}

export default function BlogStrip({
  limit = 3,
  relatedToProduct,
  heading = "From our journal",
  subheading = "Care guides, styling tips and hair stories from the Plymhair team.",
  className = "",
}: BlogStripProps) {
  const { data: posts = [], isLoading } = useBlogPosts();

  const picks = useMemo(() => {
    if (!posts.length) return [];
    if (!relatedToProduct) return posts.slice(0, limit);

    const scored = posts
      .map((post) => ({
        post,
        score: relevanceScore(
          { strong: [relatedToProduct.categories], weak: [relatedToProduct.name] },
          { strong: [post.title], weak: [post.excerpt] }
        ),
      }))
      .filter((x) => x.score >= MIN_RELEVANCE_SCORE)
      .sort((a, b) => b.score - a.score);

    const real = scored.slice(0, limit).map((x) => x.post);
    if (real.length >= limit) return real;

    const picked = new Set(real.map((p) => p.id));
    const filler = posts.filter((p) => !picked.has(p.id));
    return [...real, ...filler].slice(0, limit);
  }, [posts, relatedToProduct, limit]);

  if (isLoading || picks.length === 0) return null;

  return (
    <section className={`py-14 md:py-20 bg-secondary/30 ${className}`}>
      <div className="container max-w-6xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8 md:mb-10">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-body font-semibold uppercase tracking-[0.25em] text-primary mb-2">
              <BookOpen className="w-3 h-3" /> Hair Care Tips
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              {heading.split(" ").map((word, i, arr) =>
                i === arr.length - 1 ? (
                  <span key={i} className="italic font-normal text-primary"> {word}</span>
                ) : (
                  <span key={i}>{i === 0 ? word : ` ${word}`}</span>
                )
              )}
            </h2>
            <p className="font-body text-sm md:text-base text-muted-foreground mt-2 max-w-lg">
              {subheading}
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-xs font-body font-medium uppercase tracking-[0.12em] text-primary hover:gap-2 transition-all"
          >
            All articles <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {picks.map((post, i) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group block animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
              style={{ animationDelay: `${i * 90}ms`, animationDuration: "500ms" }}
            >
              <div className="aspect-[16/10] bg-gradient-to-br from-primary/20 to-accent/20 rounded-xl mb-4 overflow-hidden">
                {post.cover_image ? (
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-primary/40" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-[11px] font-body mb-1.5">
                <Calendar className="w-3 h-3" />
                {format(new Date(post.created_at), "d MMM yyyy")}
                <span className="mx-1">·</span>
                {post.author}
              </div>
              <h3 className="font-display text-lg font-semibold leading-snug mb-1.5 group-hover:text-primary transition-colors">
                {post.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground line-clamp-2 mb-2">
                {post.excerpt}
              </p>
              <span className="inline-flex items-center gap-1 text-primary text-xs font-body font-medium uppercase tracking-[0.1em] group-hover:gap-2 transition-all">
                Read more about {post.title} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
